import { ApolloQueryResult, QueryResult } from '@apollo/client';
import {
  Playlist,
  PlaylistsGridDocument,
  PlaylistsLandingDocument,
  usePlaylistsGridQuery
} from 'api';
import cx from 'classnames';
import { GetServerSideProps } from 'next';
import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { initializeApollo } from 'lib/graphql/client';
import { createLogger } from 'lib/logging';

import { LazyLoadAd, PlaylistAds } from 'components/ads';
import ContentContainer from 'components/content-container';
import FilterableList from 'components/filterable-list';
import Grid from 'components/grid';
import Col from 'components/grid/col';
import { Layout } from 'components/layout';
import Modal from 'components/modal';
import PageHeader from 'components/page-header';
import FeaturedPlaylists from 'components/pages/playlists/featured-playlists';
import PlaylistsHead from 'components/pages/playlists/head';
import PlaylistsGrid from 'components/pages/playlists/playlists-grid';
import PlaylistsSearchFilters, {
  CURATOR_OPTIONS
} from 'components/pages/playlists/search-filters';
import PlaylistsSearchTags from 'components/pages/playlists/search-tags';
import Pagination from 'components/pagination';
import PlaylistCardSkeleton from 'components/playlist-card/playlist-card-skeleton';
import SkipLink from 'components/skip-link';

import usePlaylistSearch from 'components/pages/playlists/use-query-helpers';

const ITEMS_PER_PAGE = 24;

export type FilterTopic = {
  name: string;
  slug: string;
};

type PlaylistsPageProps = {
  featuredPlaylists: Playlist[];
  topicList: FilterTopic[];
  initialPlaylists: Playlist[];
};

const Playlists = ({
  featuredPlaylists,
  topicList,
  initialPlaylists
}: PlaylistsPageProps) => {
  const { formatMessage: t } = useIntl();
  const filtersRef = useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const {
    getCurrentQueryParams,
    addTopic,
    removeTopic,
    setCurator,
    setActivePage,
    clearAll
  } = usePlaylistSearch(setModalOpen);

  let queryTopics: string[] = [];
  let queryCurator;
  let queryPage;

  if (typeof window !== 'undefined') {
    const currentQueryParams = getCurrentQueryParams();

    queryTopics = currentQueryParams.topics as string[];
    queryCurator = currentQueryParams.curator;
    queryPage = currentQueryParams.page
      ? parseInt(currentQueryParams.page as string, 10)
      : 1;
  }

  const { data, loading, error } = usePlaylistsGridQuery({
    variables: {
      topics: queryTopics,
      curator: queryCurator,
      after: ((queryPage - 1) * ITEMS_PER_PAGE).toString(),
      first: ITEMS_PER_PAGE
    },
    ssr: true
  });

  if (error) {
    const log = createLogger('Playlist');
    log.error('adding/removing playlist to watch later list failed', error);
  }

  const pagesNum = Math.ceil(
    (data?.playlists?.totalCount || 0) / ITEMS_PER_PAGE
  );

  const filterWrapperClasses = cx(
    'lg:mb-13 outline-inside mb-5 mt-2 font-bold md:mb-3 md:mt-4 lg:mt-12',
    {
      'mb-0 md:mb-0 lg:mb-0': queryTopics?.length > 0 || queryCurator
    }
  );

  return (
    <Layout>
      <PlaylistsHead />
      <LazyLoadAd>
        <PlaylistAds.PlaylistAdBillboard />
      </LazyLoadAd>
      <div className="w-full bg-white text-black">
        <PageHeader
          title={t({
            defaultMessage: 'Playlists',
            id: 'playlists.header.title'
          })}
          subtitle={t({
            defaultMessage: 'TED Talk collections for curious minds',
            id: 'playlists.header.subtitle'
          })}
        />
        <LazyLoadAd>
          <PlaylistAds.PlaylistIndexAdInlineATFMobile />
        </LazyLoadAd>
        {featuredPlaylists.length > 0 && (
          <ContentContainer className="mt-10 md:mt-12 xl:mt-20">
            <SkipLink to="#filters">
              <FormattedMessage defaultMessage="Skip featured playlists" />
            </SkipLink>
            <FeaturedPlaylists playlists={featuredPlaylists} />
          </ContentContainer>
        )}

        <div
          id="filters"
          role="group"
          tabIndex={-1}
          aria-label="Playlists filters"
          className={filterWrapperClasses}
          ref={filtersRef}
        >
          <ContentContainer className="bg-gray-50 py-5 shadow-sm md:py-8 xl:py-11">
            <PlaylistsSearchFilters
              setCurator={setCurator}
              addTopic={addTopic}
              setModalOpen={() => setModalOpen(true)}
              topics={getCurrentQueryParams()?.topics as string[]}
              curator={getCurrentQueryParams()?.curator as string[]}
            />
          </ContentContainer>
          {(queryTopics?.length > 0 || queryCurator) && (
            <ContentContainer className="mt-10 md:mt-14 lg:mt-16">
              <PlaylistsSearchTags
                topics={queryTopics}
                curator={queryCurator}
                removeTopic={removeTopic}
                clearAll={clearAll}
                setCurator={setCurator}
              />
            </ContentContainer>
          )}
        </div>

        <div className="pb-2 pt-5 md:pb-4 md:pt-9 lg:pt-7 xl:pb-12">
          <ContentContainer>
            {loading ? (
              <Grid>
                {[...new Array(ITEMS_PER_PAGE)].map((_, index) => (
                  <Col sm={2} md={3} lg={2} key={index}>
                    <PlaylistCardSkeleton
                      size="s"
                      className="mb-8"
                      numberOfDescriptionLines={0}
                    />
                  </Col>
                ))}
              </Grid>
            ) : (
              <PlaylistsGrid
                playlists={(data?.playlists?.nodes || initialPlaylists).filter(
                  (playlist): playlist is Playlist => Boolean(playlist)
                )}
                error={error || null}
              />
            )}
          </ContentContainer>
        </div>

        {pagesNum > 1 && !loading && !error && (
          <div className="mb-10 self-center md:mb-12 xl:mb-20">
            <Pagination
              totalPageCount={pagesNum}
              activeIndex={queryPage}
              onChange={(index, e) => {
                e?.preventDefault();
                requestAnimationFrame(() => {
                  filtersRef.current?.scrollIntoView({
                    behavior: 'smooth'
                  });
                });
                setActivePage(index);
              }}
            />
          </div>
        )}

        <Modal
          isOpen={modalOpen}
          onDismiss={() => setModalOpen(false)}
          ariaLabel="Topics"
          noPadding
          noOverflow
          stickyCloseButton
        >
          <div className="md:absolute md:inset-0">
            <FilterableList
              isInModal
              title="Topics"
              list={topicList.map(topic => ({
                label: topic.name,
                value: topic
              }))}
              onItemClick={addTopic}
              searchInputPlaceholder="Search by topic"
              selectedTopics={getCurrentQueryParams()?.topics as string[]}
            />
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader('Cache-Control', 'no-transform, public, max-age=180');
  const apolloClient = initializeApollo();
  const log = createLogger('Playlists');

  // curator query parameter is an ENUM type, we avoid forwarding wrong value to query
  const checkQueryCurator = queryCurator => {
    const isCuratorValid = CURATOR_OPTIONS.find(
      curatorOption =>
        curatorOption.value.toLowerCase() === queryCurator?.toLowerCase()
    );

    return isCuratorValid ? queryCurator : null;
  };

  let playlistsGridData;
  let playlistsLandingData;

  try {
    // Perform playlist queries in parallel
    const [gridResult, landingResult] = await Promise.allSettled([
      apolloClient.query({
        query: PlaylistsGridDocument,
        variables: {
          topics: context.query.topics,
          curator: checkQueryCurator(context.query.curator),
          page: context.query.page,
          first: ITEMS_PER_PAGE
        }
      }),
      apolloClient.query({
        query: PlaylistsLandingDocument
      })
    ]);

    playlistsGridData = extractSettledValue(
      log,
      'playlist grid data',
      gridResult
    );

    playlistsLandingData = extractSettledValue(
      log,
      'playlist landing data',
      landingResult
    );
  } catch (e) {
    // Logging errors, but data fetching will silently fail and return empty set
    log.error('getting playlists failed', e);
  }

  const featuredPlaylists =
    playlistsLandingData?.featuredPlaylists?.nodes || [];
  const topicList = playlistsLandingData?.topicList?.nodes || [];
  const initialPlaylists: Playlist[] =
    playlistsGridData?.playlists?.nodes || [];

  return {
    props: {
      featuredPlaylists,
      topicList,
      initialPlaylists
    }
  };
};

function extractSettledValue(
  logger: ReturnType<typeof createLogger>,
  logPrefix = 'playlist data',
  result: PromiseSettledResult<ApolloQueryResult<QueryResult>>
) {
  if (result.status === 'rejected') {
    logger.error(`getting ${logPrefix} failed at promise`, result.reason);
    return {};
  }

  const { value: response } = result;
  if (response.error) {
    logger.error(`getting ${logPrefix} failed in data`, response.error);
  }

  return response.data;
}

export default Playlists;
